import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { BasicDatepickerComponent } from '../../ui-elements/datepicker/basic-datepicker/basic-datepicker.component';
import { BasicHeatmapChartComponent } from '../../apexcharts/more-charts/basic-heatmap-chart/basic-heatmap-chart.component';
import { DateTime } from 'luxon';
import { ActivityReportService } from './service/activity-reports.service';
import { ActivityReportBySerial, ActivityReportChartItem } from './models/activity-reports.models';
import { forkJoin, of, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivityTotalsComponent } from './activity-totals/activity-totals.component';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MultipleYaxisChartComponent } from '../../apexcharts/mixed-charts/multiple-yaxis-chart/multiple-yaxis-chart.component';
import { AdminPowerPriceService } from './service/AdminPowerPrice.service';
import { catchError } from 'rxjs/operators';
import { GetElPricesByUserIdResponse } from '../../protos/generated/admin.pb';
@Component({
  selector: 'app-activity-reports',
  imports: [
    CommonModule,
    RouterLink,
    BasicDatepickerComponent,
    BasicHeatmapChartComponent,
    MatIconModule,
    MatCardModule,
    MatProgressSpinnerModule,
    ActivityTotalsComponent,
    TranslatePipe,
    MultipleYaxisChartComponent
  ],
  templateUrl: './activity-reports.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './activity-reports.component.scss'
})
export class ActivityReportsComponent implements OnInit, OnDestroy {

  userId!: number;
  serialNumber!: string;
  selectedDate?: DateTime;
  plantId?: number;
  loading = false;
  error: string | null = null;
  noDataForUser = false;
  reportBucket?: ActivityReportBySerial | undefined;
  chartItems: ActivityReportChartItem[] = [];
  private sub?: Subscription;
  totalBatteryCapacityKwh?: string;
  inverterIdFromNav?: number;

  private getBatteryCapacity(): number {
    const v = Number(this.totalBatteryCapacityKwh);
    return Number.isFinite(v) ? v : 0;
  }

  constructor(
    private route: ActivatedRoute,
    private readonly activityReportService: ActivityReportService,
    private router: Router,
    private readonly adminPowerPriceService: AdminPowerPriceService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.userId = Number(params['userId']);
      this.serialNumber = params['serialNumber'];
      this.plantId = Number(params['plantId']);
    });
    const st = history.state as any;
    this.totalBatteryCapacityKwh ??= st?.totalBatteryCapacityKwh;
    this.inverterIdFromNav ??= st?.inverterId;
  }

  goPlantsDetails() {
    this.router.navigate(['plants/plants-detail', this.plantId]);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onDateChange(date: Date): void {
    if (!date) return;

    const base = DateTime.fromObject(
      {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      },
      { zone: 'utc' }
    );

    const start = base.startOf('day');
    const end = base.endOf('day');

    this.selectedDate = base;
    this.fetchActivityReport(start, end);
  }

  private fetchActivityReport(start: DateTime, end: DateTime): void {
    if (!this.userId || !this.serialNumber) return;

    this.loading = true;
    this.error = null;
    this.noDataForUser = false;
    this.chartItems = [];
    this.sub?.unsubscribe();

    const dateStr = start.toFormat('yyyy-LL-dd');

    this.sub = forkJoin({
      report: this.activityReportService.getActivityReportForSerial(
        this.userId,
        start,
        end,
        this.serialNumber
      ),
      prices: this.adminPowerPriceService.getPowerPriceByUserId(dateStr, this.userId).pipe(
        catchError(() => of(null))
      )
    }).subscribe({
      next: ({ report, prices }) => {
        this.loading = false;
        this.reportBucket = report;
        this.noDataForUser = !report || !report.items?.length;
        if (!report) {
          this.chartItems = [];
          return;
        }

        const activityItems = this.mapBucketToChartItems(report);
        const activityByHour = new Map<number, ActivityReportChartItem>();
        for (const it of activityItems) activityByHour.set(it.hour, it);

        const breakdown = this.buildElectricityPriceBreakdown(
          prices,
          this.serialNumber
        );
        const dayPriceByHour = breakdown.combinedByHour;

        const capacity = this.getBatteryCapacity();
        this.chartItems = Array.from({ length: 24 }, (_, h) => {
          const base = activityByHour.get(h);
          const reportPrice = base?.electricityPrice ?? 0;
          const fallbackPrice = dayPriceByHour.get(h) ?? 0;
          const electricityPrice =
            reportPrice !== 0 ? reportPrice : fallbackPrice;

          return {
            hour: h,
            chargedKwh: base?.chargedKwh ?? 0,
            dischargedKwh: base?.dischargedKwh ?? 0,
            electricityPrice,
            totalPrice: base?.totalPrice ?? 0,
            acCharge: base?.acCharge,
            chargeIsSet: base?.chargeIsSet ?? null,
            dischargeIsSet: base?.dischargeIsSet ?? null,
            dischargeLimitPercentage: base?.dischargeLimitPercentage ?? 0,
            chargeLimitPercentage: base?.chargeLimitPercentage ?? null,
            solarProductionKwh: base?.solarProductionKwh ?? 0,
            chargedFromGridKwh: base?.chargedFromGridKwh ?? 0,
            soc: base?.soc,
            totalBatteryCapacityKwh: capacity,
            consumptionKwh: base?.consumptionKwh ?? null,
          } as ActivityReportChartItem;
        });

        this.noDataForUser = !activityItems.length;
      },
      error: (err) => {
        this.loading = false;
        this.chartItems = [];

        const statusCode = (err as any)?.statusCode ?? (err as any)?.code;
        const statusMessage =
          (err as any)?.statusMessage ??
          (err as any)?.details ??
          (err as any)?.message ??
          '';

        if (
          statusCode === 5 &&
          typeof statusMessage === 'string' &&
          statusMessage.includes('No Sharing model data found')
        ) {
          this.noDataForUser = true;
          this.error =
            'No activity data found for this user on the selected date. ' +
            'Try choosing another date or check that smart charging is enabled for this inverter.';
        } else {
          this.noDataForUser = true;
          this.error =
            'Something went wrong while loading the activity report. Please try again.';
        }
      }
    });
  }

  private buildElectricityPriceBreakdown(
    prices: GetElPricesByUserIdResponse | null,
    serialNumber: string
  ): { spotByHour: Map<number, number>; tariffByHour: number[]; combinedByHour: Map<number, number> } {
    const combinedByHour = new Map<number, number>();
    const spotByHour = new Map<number, number>();
    const tariffByHour = new Array<number>(24).fill(0);
    
    if (!prices) return { spotByHour, tariffByHour, combinedByHour };

    // Build simple spot price map
    if (prices?.spotPriceItems?.length) {
      for (const p of prices.spotPriceItems as any[]) {
        const hour = p.hour ?? p._hour;
        const dkk = p.dkkPerKwh ?? p._dkkPerKwh;
        const v = Number(dkk);
        if (typeof hour === 'number') {
          spotByHour.set(hour, Number.isFinite(v) ? v : 0);
        }
      }
    }

    // Extract tariff by hour
    const extractedTariffByHour = this.extractTariffByHour(prices, serialNumber);
    for (let h = 0; h < 24; h++) tariffByHour[h] = extractedTariffByHour[h] ?? 0;

    // Combine spot + tariff
    for (let h = 0; h < 24; h++) {
      const spot = spotByHour.get(h) ?? 0;
      const tariff = tariffByHour[h] ?? 0;
      combinedByHour.set(h, spot + tariff);
    }

    return { spotByHour, tariffByHour, combinedByHour };
  }

  private extractTariffByHour(
    prices: GetElPricesByUserIdResponse,
    serialNumber: string
  ): number[] {
    const tariffByHour = new Array<number>(24).fill(0);

    const tariffResponse: any =
      (prices as any)?.eloverblikTarrifPrices ?? (prices as any)?._eloverblikTarrifPrices;
    const inverterItems: any[] =
      tariffResponse?.eloverblikTarrifInverterItems ??
      tariffResponse?._eloverblikTarrifInverterItems ??
      [];
    if (!inverterItems.length) return tariffByHour;

    const matchingInverter: any =
      inverterItems.find(i =>
        String((i as any)?.serialNumber ?? (i as any)?._serialNumber ?? '') === serialNumber
      ) ?? inverterItems[0];

    const preferredMeteringPointId: string | null = (() => {
      const ids: any[] =
        (matchingInverter as any)?.meteringPointIds ??
        (matchingInverter as any)?._meteringPointIds ??
        [];
      const id = ids[0];
      return typeof id === 'string' && id.trim() ? id.trim() : null;
    })();

    let tariffItems: any[] =
      matchingInverter?.eloverblikTariffItems ??
      matchingInverter?._eloverblikTariffItems ??
      [];

    if (preferredMeteringPointId) {
      const filtered = tariffItems.filter(ti =>
        String((ti as any)?.meteringPointId ?? (ti as any)?._meteringPointId ?? '') === preferredMeteringPointId
      );
      if (filtered.length) tariffItems = filtered;
    }

    const totalCandidates = tariffItems.filter(ti => {
      const title = String((ti as any)?.title ?? (ti as any)?._title ?? '').toLowerCase();
      return title.includes('total') || title.includes('samlet') || title.includes('sum');
    });
    if (totalCandidates.length) tariffItems = totalCandidates;

    for (const ti of tariffItems) {
      const tariffArr: any[] = (ti as any)?.tariff ?? (ti as any)?._tariff ?? [];
      for (let h = 0; h < 24 && h < tariffArr.length; h++) {
        const raw = Number(tariffArr[h]);
        tariffByHour[h] += this.normalizeTariffDkkPerKwh(raw);
      }
    }

    return tariffByHour;
  }

  private normalizeTariffDkkPerKwh(raw: number): number {
    if (!Number.isFinite(raw)) return 0;

    const abs = Math.abs(raw);
    if (abs <= 5) return raw;

    // Heuristics: tariff may arrive in øre/kWh (e.g. 25.7) → DKK/kWh.
    if (abs <= 500) return raw / 100;

    // Or DKK/MWh (e.g. 257.8) → DKK/kWh.
    return raw / 1000;
  }

  private mapBucketToChartItems(bucket: ActivityReportBySerial): ActivityReportChartItem[] {
    if (!bucket.items?.length) return [];
    const capacity = this.getBatteryCapacity();
    return [...bucket.items]
      .sort((a, b) => a.hour - b.hour)
      .map(i => ({
        hour: i.hour,
        chargedKwh: i.chargedKwh ?? 0,
        dischargedKwh: i.dischargedKwh ?? 0,
        electricityPrice: i.electricityPrice ?? 0,
        totalPrice: i.totalPrice ?? 0,
        acCharge: i.acCharge,
        chargeIsSet: i.chargeIsSet ?? null,
        dischargeIsSet: i.dischargeIsSet ?? null,
        dischargeLimitPercentage: i.dischargeLimitPercentage ?? 0,
        chargeLimitPercentage: i.chargeLimitPercentage ?? null,
        solarProductionKwh: i.solarProductionKwh ?? 0,
        chargedFromGridKwh: i.chargedFromGridKwh ?? 0,
        soc: i.soc,
        totalBatteryCapacityKwh: capacity,
        consumptionKwh: i.consumptionKwh ?? null,
      }));
  }

}
